import { Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ScrollToTop } from '@/components/ScrollToTop';
import {
  HomePage, ServicePage, DistrictPage, ProjectPage, BlogPost, AuthorPage,
  StandardPage, LegalPage, LeadPage, NotFoundPage,
} from '@/templates';
import { getPage, getProject, ancestorsOf, placeName, type ContentPage } from '@/lib/content';
import { buildHead, useHead } from '@/lib/seo';
import * as schema from '@/lib/schema';
import { has, site } from '@/config/site';
import { GlossaryHub, GlossaryTermPage } from '@/components/Glossary';
import { GLOSSARY_SLUG, getGlossaryPage } from '@/lib/glossary';
import { glossaryHubHead, glossaryTermHead } from '@/lib/glossary-head';

/** Страниците, на които формата стои горе вдясно, а не само в дъното. */
const LEAD_PAGES = new Set(['/bezplaten-ogled', '/kontakti']);

function headFor(page: ContentPage): ReturnType<typeof buildHead> {
  // Началната страница няма пътека - тя е коренът ѝ.
  const trail =
    page.slug === '/'
      ? []
      : [
          { name: 'Начало', slug: '/' },
          ...ancestorsOf(page.slug).filter((item) => item.slug !== '/').map((item) => ({ name: item.name, slug: item.slug })),
          { name: page.name, slug: page.slug },
        ];

  const common = [schema.organization(), schema.website(), schema.breadcrumbs(trail), schema.faqPage(page.faq)];

  let nodes = common;
  if (page.type === 'Service' || page.type === 'Service hub' || page.type === 'Pricing') {
    nodes = [...common, schema.service(page)];
  } else if (page.type === 'District') {
    nodes = [...common, schema.service(page, placeName(page))];
  } else if (page.type === 'Blog post') {
    nodes = [...common, schema.article(page), schema.person()];
  } else if (page.type === 'Author') {
    nodes = [...common, schema.person(), schema.profilePage()];
  } else if (page.slug === '/kontakti') {
    nodes = [...common, schema.contactPage()];
  }

  // Калкулаторите са инструменти, а не само текст. Маркерът в съдържанието го показва.
  if (/data-widget="(kvadratura|kalkulator)"/.test(page.html)) nodes = [...nodes, schema.webApplication(page)];

  return buildHead({
    title: page.metaTitle || page.h1,
    description: page.metaDescription || page.description,
    slug: page.slug,
    canonicalUrl: page.canonicalUrl,
    ogType: page.type === 'Blog post' ? 'article' : 'website',
    noindex: page.noindex,
    jsonLd: schema.graph(...nodes),
    publishedTime: page.publishDate || undefined,
    modifiedTime: page.updated || undefined,
  });
}

function renderPage(page: ContentPage) {
  if (page.slug === '/') return <HomePage page={page} />;
  if (LEAD_PAGES.has(page.slug)) return <LeadPage page={page} />;

  switch (page.type) {
    case 'Home':
      return <HomePage page={page} />;
    case 'Service':
    case 'Service hub':
    case 'Pricing':
      return <ServicePage page={page} />;
    case 'District':
      return <DistrictPage page={page} />;
    case 'Blog post':
      return <BlogPost page={page} />;
    case 'Author':
      return <AuthorPage page={page} />;
    case 'Legal':
      return <LegalPage page={page} />;
    default:
      return <StandardPage page={page} />;
  }
}

function PageView() {
  const { pathname } = useLocation();
  const slug = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  // Речникът не е страница от Notion: хъбът и отделните термини идват от src/data/rechnik.ts.
  const isGlossaryHub = slug === GLOSSARY_SLUG;
  const glossarySlug = slug.startsWith(`${GLOSSARY_SLUG}/`) ? slug.slice(GLOSSARY_SLUG.length + 1) : '';
  const glossaryPage = glossarySlug ? getGlossaryPage(glossarySlug) : undefined;

  const page = isGlossaryHub || glossaryPage ? undefined : getPage(slug);
  const project = page || isGlossaryHub || glossaryPage ? undefined : getProject(slug);

  const head = isGlossaryHub
    ? glossaryHubHead()
    : glossaryPage
      ? glossaryTermHead(glossaryPage.slug)!
    : page
    ? headFor(page)
    : project
      ? buildHead({
          title: `${project.title} — реализиран обект`,
          description:
            project.description ||
            `${project.title}. ${[project.district, project.area ? `${project.area} м²` : '', project.duration].filter(Boolean).join(' · ')}`,
          slug: project.slug,
          ogImage: project.imageAfter ? `${__SITE_URL__}${project.imageAfter.src}` : undefined,
          jsonLd: schema.graph(
            schema.organization(),
            schema.website(),
            schema.breadcrumbs([
              { name: 'Начало', slug: '/' },
              { name: 'Проекти', slug: '/proekti' },
              { name: project.title, slug: project.slug },
            ]),
            schema.projectWork(project),
          ),
        })
      : buildHead({
          title: `Страницата не е намерена | ${has('companyName') ? site.companyName : 'Ремонт на покриви София'}`,
          description: 'Такава страница няма. Вижте услугите, цените и районите, в които работим.',
          slug,
          noindex: true,
        });

  useHead(head);

  if (isGlossaryHub) return <GlossaryHub />;
  if (glossaryPage) return <GlossaryTermPage slug={glossaryPage.slug} />;
  if (page) return renderPage(page);
  if (project) return <ProjectPage project={project} />;
  return <NotFoundPage />;
}

export default function App() {
  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        <Route path="*" element={<PageView />} />
      </Routes>
    </Layout>
  );
}
